// deployment_class: customer_env_allowed
// @kathedral-criticality: high
/**
 * Turn any thrown value into a failure a customer can act on — without ever
 * letting it be mistaken for an answer, and without leaking internals.
 *
 * WHY THIS IS ITS OWN MODULE. The Ask overlay used to do this inline, and did it
 * two ways that both mattered: it appended failures as `role: 'assistant'`
 * messages, and it interpolated the raw `err.message` into them. The first is a
 * provenance defect — the server holds no such assistant turn, yet it HAS
 * persisted the user's question (ConversationManager inserts it before the model
 * call), so the client and server transcripts diverge silently and the model
 * never knows it "said" anything. The second hands internal detail to a PHA CEO
 * (N64).
 *
 * The messages here are FIXED STRINGS, chosen per cause. Nothing from the error
 * is interpolated — not the message, not a code, not a host. That is a deliberate
 * trade: a slightly less specific message to a user, in exchange for a leak being
 * structurally impossible rather than a review item on every future edit.
 *
 * Specificity still matters where it is ACTIONABLE. A budget cap is something the
 * user can understand and wait out, so it says so and says when. An unknown fault
 * is not, so it says the honest thing and invents nothing — per the standing rule
 * that we guide users to accurate means, an invented cause is worse than an
 * admitted unknown.
 */
export type AskFailureKind =
  | 'offline'
  | 'budget'
  | 'signed_out'
  | 'not_permitted'
  | 'unavailable'
  | 'invalid'
  | 'unknown';

export interface AskFailure {
  kind: AskFailureKind;
  /** Shown to the user. Always a fixed string — never derived from the error. */
  userMessage: string;
  /** Whether trying the same request again could plausibly succeed. */
  retryable: boolean;
}

const MESSAGES: Record<AskFailureKind, string> = {
  offline:
    'Kathedral could not reach the network. Check your connection and try again.',
  budget:
    "You've reached today's AI usage limit for this area. It resets at midnight UTC.",
  signed_out: 'Your session has ended. Sign in again to continue.',
  not_permitted: 'Your account does not have access to Kathedral AI on this screen.',
  unavailable:
    'Kathedral AI is temporarily unavailable. Nothing was answered — please try again shortly.',
  invalid: 'That request could not be processed. Try rephrasing your question.',
  unknown:
    'Kathedral AI could not complete that request. Nothing was answered — please try again.',
};

function failure(kind: AskFailureKind, retryable: boolean): AskFailure {
  return { kind, userMessage: MESSAGES[kind], retryable };
}

/**
 * The error codes a CSRF refusal sends (UI-B9).
 *
 * SSOT is `src/v3/server/middleware/csrf.ts:136,146`. The frontend cannot import
 * across that build boundary, so the literals are duplicated here and PINNED by
 * `test/shell/ai/ask-failure.test.ts` — the same convention `CSRF_COOKIE_NAME`
 * already uses in `api-client-base.ts`. Rename one side without the other and a
 * test fails, rather than users silently being told the wrong thing again.
 */
export const CSRF_REFUSAL_CODES = ['csrf_token_mismatch', 'csrf_token_invalid'] as const;

/**
 * True when a 403 came from CSRF rather than from a scope check.
 *
 * Read by SHAPE for the same reason the status is — a mocked or re-bundled
 * `ApiError` must not take the classifier down. `parseProblemDetail` puts the
 * raw response body on `.problem`, so a CSRF refusal arrives as
 * `{ error: 'csrf_token_mismatch' }`.
 */
function isCsrfRefusal(err: unknown): boolean {
  const code = (err as { problem?: { error?: unknown } } | null)?.problem?.error;
  return typeof code === 'string' && (CSRF_REFUSAL_CODES as readonly string[]).includes(code);
}

/**
 * Classify a thrown value. Total by construction: every input, including `null`,
 * a bare string or a number, yields a usable failure — a silent or empty failure
 * state is how a spinner runs forever.
 */
export function classifyAskFailure(err: unknown): AskFailure {
  // A fetch rejection is a connection problem, not an AI problem. Naming it
  // correctly stops a user reporting "the AI is broken" when their wifi dropped.
  if (err instanceof TypeError && /fetch|network/i.test(err.message)) {
    return failure('offline', true);
  }

  // Shape check, NOT `instanceof ApiError`. Identity across a module boundary is
  // fragile — a test that mocks api-client-base without re-exporting the class,
  // a split bundle, or a second realm all break `instanceof` and would take the
  // classifier down with them (found exactly that way: the partnership panel's
  // suite mocks that module, and an instanceof check threw during render). A
  // numeric `status` is what we actually depend on, so that is what is tested,
  // and it works for any error carrying one — not only ours.
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status === 'number') {
    const s = status;
    if (s === 429) return failure('budget', false);
    if (s === 401) return failure('signed_out', false);
    // UI-B9 · a 403 is TWO different events and they need different answers.
    // A CSRF refusal means the session is gone — the token is a cookie bound to
    // it at creation, so there is nothing to refresh and nothing to retry.
    // Saying "you don't have access" there is FALSE, and it sends a user to
    // request access they already hold. Worse, csrf.ts:22 revokes the session
    // after 3 mismatches in an hour, so a retry walks them toward revocation.
    // `signed_out` is both true and actionable: sign in again.
    if (s === 403 && isCsrfRefusal(err)) return failure('signed_out', false);
    if (s === 403) return failure('not_permitted', false);
    // 5xx includes the AI-upstream-auth case (a bad or missing provider key).
    // To the user that is simply "unavailable" — the cause is ours to fix and
    // theirs to be spared; surfacing it would also disclose configuration state.
    if (s >= 500) return failure('unavailable', true);
    if (s >= 400) return failure('invalid', false);
  }

  return failure('unknown', false);
}
